import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, XCircle, RefreshCw, BookOpen } from "lucide-react";
import { toast } from "sonner";

const quizData = {
  1: [
    {
      question: "¿Qué es un nodo en n8n?",
      answers: [
        "Una unidad de trabajo que realiza una acción específica",
        "Una variable",
        "Un archivo",
        "Una base de datos",
      ],
      correct: 0,
    },
    {
      question: "¿Qué tipo de nodo necesitas para iniciar un workflow?",
      answers: [
        "Un nodo End",
        "Un nodo Trigger",
        "Un nodo Code",
        "Cualquier nodo",
      ],
      correct: 1,
    },
    {
      question: "¿Cómo se conectan los nodos?",
      answers: [
        "Con cables físicos",
        "Arrastrando desde los puntos de conexión",
        "Automáticamente",
        "Escribiendo código",
      ],
      correct: 1,
    },
    {
      question: "¿Qué nodo inicia un workflow manualmente?",
      answers: ["Begin", "Manual Trigger", "Init", "Launch"],
      correct: 1,
    },
    {
      question: "¿Cómo termina un workflow en n8n?",
      answers: [
        "Con un nodo End obligatorio",
        "Con un nodo Stop",
        "Ejecuta hasta el último nodo conectado",
        "Con un nodo Close",
      ],
      correct: 2,
    },
    {
      question: "¿Qué es un workflow (flujo de trabajo)?",
      answers: [
        "Una secuencia de nodos conectados",
        "Un tipo de nodo",
        "Una herramienta de diseño",
        "Un lenguaje de programación",
      ],
      correct: 0,
    },
    {
      question: "¿Pueden los nodos tener múltiples conexiones?",
      answers: [
        "No, solo una",
        "Sí, pueden tener varias",
        "Solo dos",
        "Depende del tipo",
      ],
      correct: 1,
    },
    {
      question: "¿Por dónde fluyen los datos en n8n?",
      answers: [
        "De derecha a izquierda",
        "De abajo hacia arriba",
        "A través de las conexiones entre nodos",
        "No fluyen",
      ],
      correct: 2,
    },
    {
      question: "¿Qué formato de datos usa n8n internamente?",
      answers: ["XML", "JSON", "CSV", "YAML"],
      correct: 1,
    },
    {
      question: "¿Cuál es el propósito principal de n8n?",
      answers: [
        "Crear páginas web",
        "Automatizar tareas y flujos de trabajo",
        "Diseñar gráficos",
        "Programar videojuegos",
      ],
      correct: 1,
    },
  ],
  2: [
    {
      question: "¿Cómo se llama oficialmente el nodo Set en n8n actual?",
      answers: [
        "Set Node",
        "Edit Fields (Set)",
        "Data Editor",
        "Variable Node",
      ],
      correct: 1,
    },
    {
      question: "¿Para qué sirve el nodo Edit Fields?",
      answers: [
        "Para establecer y modificar campos de datos",
        "Para eliminar workflows",
        "Para crear funciones",
        "Para enviar emails",
      ],
      correct: 0,
    },
    {
      question: "¿Qué permite hacer el nodo Edit Fields?",
      answers: [
        "Preparar datos",
        "Crear variables",
        "Modificar información",
        "Todas las anteriores",
      ],
      correct: 3,
    },
    {
      question: "¿Qué modos tiene el nodo Edit Fields?",
      answers: [
        "Solo Manual",
        "Manual Mapping y JSON Output",
        "Solo JSON",
        "Automático únicamente",
      ],
      correct: 1,
    },
    {
      question: "¿En qué orden deben conectarse los nodos?",
      answers: [
        "Cualquier orden",
        "Trigger primero, luego los demás",
        "Edit Fields primero",
        "No importa",
      ],
      correct: 1,
    },
    {
      question: "¿Cuál es un flujo básico correcto?",
      answers: [
        "Edit Fields → Trigger",
        "Manual Trigger → Edit Fields → HTTP Request",
        "HTTP Request → Trigger",
        "Code → Trigger",
      ],
      correct: 1,
    },
    {
      question: "¿Puedes tener múltiples nodos Edit Fields?",
      answers: ["No", "Sí", "Solo dos", "Depende"],
      correct: 1,
    },
    {
      question:
        "¿Qué opción permite descartar campos no usados en Edit Fields?",
      answers: [
        "Delete All",
        "Include Other Input Fields: false",
        "Clear Data",
        "No existe tal opción",
      ],
      correct: 1,
    },
    {
      question: "¿Por qué es útil el nodo Edit Fields?",
      answers: [
        "Para decorar",
        "Para preparar datos antes de enviarlos a otros servicios",
        "Para cerrar el flujo",
        "No es útil",
      ],
      correct: 1,
    },
    {
      question: "¿Qué representa una conexión entre nodos?",
      answers: [
        "El paso de datos",
        "Un cable físico",
        "Una función",
        "Un error",
      ],
      correct: 0,
    },
  ],
  3: [
    {
      question: "¿Qué hace el nodo IF?",
      answers: [
        "Evalúa condiciones y divide el flujo en dos ramas",
        "Elimina datos",
        "Suma números",
        "Envía emails",
      ],
      correct: 0,
    },
    {
      question: "¿Cuántas salidas tiene un nodo IF?",
      answers: ["1", "2 (True y False)", "3", "4"],
      correct: 1,
    },
    {
      question: "¿Qué rama se ejecuta cuando la condición es verdadera?",
      answers: ["False", "True", "Both", "None"],
      correct: 1,
    },
    {
      question: "¿Qué nodo usar si necesitas más de 2 salidas condicionales?",
      answers: ["Múltiples IF", "Switch", "Merge", "Split"],
      correct: 1,
    },
    {
      question: "¿Puedes combinar múltiples condiciones en un IF?",
      answers: ["No", "Sí, con AND u OR", "Solo con AND", "Solo con OR"],
      correct: 1,
    },
    {
      question: "¿Qué es la ramificación?",
      answers: [
        "Un error",
        "Dividir el flujo en múltiples caminos",
        "Cerrar el flujo",
        "Una variable",
      ],
      correct: 1,
    },
    {
      question: "¿Qué operadores soporta el nodo IF?",
      answers: [
        "Solo =",
        "Solo >",
        "Varios (equals, contains, greater than, etc.)",
        "Ninguno",
      ],
      correct: 2,
    },
    {
      question: "¿Qué tipos de datos puede comparar el nodo IF?",
      answers: [
        "Solo texto",
        "String, Number, Boolean, Date & Time",
        "Solo números",
        "Solo booleanos",
      ],
      correct: 1,
    },
    {
      question: "¿Qué sucede si la condición es FALSE?",
      answers: [
        "El workflow se detiene",
        "Sigue por la rama False",
        "Da error",
        "Se reinicia",
      ],
      correct: 1,
    },
    {
      question: "¿Para qué sirve la lógica condicional?",
      answers: [
        "Para decorar",
        "Para tomar decisiones automáticas basadas en datos",
        "Para eliminar datos",
        "No sirve",
      ],
      correct: 1,
    },
  ],
  4: [
    {
      question: "¿Qué hace el nodo HTTP Request?",
      answers: [
        "Envía emails",
        "Realiza solicitudes HTTP a APIs y servicios externos",
        "Crea archivos",
        "Suma números",
      ],
      correct: 1,
    },
    {
      question: "¿Qué método HTTP se usa para obtener datos?",
      answers: ["POST", "DELETE", "GET", "PUT"],
      correct: 2,
    },
    {
      question: "¿Qué método HTTP se usa para enviar/crear datos?",
      answers: ["GET", "POST", "READ", "FETCH"],
      correct: 1,
    },
    {
      question: "¿Qué necesitas configurar en HTTP Request?",
      answers: ["Una URL", "El método HTTP", "Ambas anteriores", "Nada"],
      correct: 2,
    },
    {
      question: "¿Qué es una API?",
      answers: [
        "Un tipo de nodo",
        "Una interfaz para comunicarse con servicios externos",
        "Un lenguaje",
        "Un error",
      ],
      correct: 1,
    },
    {
      question: "¿HTTP Request puede agregar headers personalizados?",
      answers: ["No", "Sí", "Solo uno", "Nunca"],
      correct: 1,
    },
    {
      question: "¿Qué formato de respuesta es más común en APIs?",
      answers: ["PDF", "JSON", "TXT", "DOC"],
      correct: 1,
    },
    {
      question: "¿Qué código HTTP indica éxito?",
      answers: ["404", "500", "200", "100"],
      correct: 2,
    },
    {
      question: '¿Qué código HTTP indica "no encontrado"?',
      answers: ["200", "404", "500", "301"],
      correct: 1,
    },
    {
      question: "¿Para qué sirve el nodo HTTP Request?",
      answers: [
        "Solo para GET",
        "Conectar con APIs y servicios web externos",
        "Eliminar datos",
        "No sirve para nada útil",
      ],
      correct: 1,
    },
  ],
  5: [
    {
      question: "¿Qué lenguajes soporta el nodo Code?",
      answers: [
        "Solo Python",
        "Solo Java",
        "JavaScript y Python",
        "C++ y Rust",
      ],
      correct: 2,
    },
    {
      question: "¿Para qué sirve el nodo Code?",
      answers: [
        "Transformaciones y lógica personalizada",
        "Solo enviar emails",
        "Solo crear imágenes",
        "Nada",
      ],
      correct: 0,
    },
    {
      question: "¿Qué modos de ejecución tiene el nodo Code?",
      answers: [
        "Solo uno",
        "Run Once for All Items y Run Once for Each Item",
        "Solo por item",
        "No tiene modos",
      ],
      correct: 1,
    },
    {
      question:
        '¿Qué variable accede a los datos en modo "Run Once for All Items"?',
      answers: ["$data", "$input.all()", "$item", "$value"],
      correct: 1,
    },
    {
      question:
        '¿Qué variable accede a los datos en modo "Run Once for Each Item"?',
      answers: ["$input.all()", "$json", "$data", "$allItems"],
      correct: 1,
    },
    {
      question: "¿Qué debe retornar tu código JavaScript?",
      answers: [
        "Nada",
        "Un array de objetos con la estructura de n8n",
        "Solo texto",
        "Solo números",
      ],
      correct: 1,
    },
    {
      question: "¿Puedes usar librerías npm externas en n8n Cloud?",
      answers: [
        "Sí, todas",
        "No, solo las built-in",
        "Solo algunas",
        "Depende del plan",
      ],
      correct: 1,
    },
    {
      question: "¿Qué nodo reemplazó el nodo Code?",
      answers: [
        "El nodo Function antiguo fue reemplazado por Code",
        "Ninguno",
        "Set",
        "HTTP Request",
      ],
      correct: 0,
    },
    {
      question: "¿Puedes hacer llamadas HTTP dentro del nodo Code?",
      answers: [
        "Sí, directamente",
        "No, debes usar el nodo HTTP Request",
        "Solo con Python",
        "Nunca",
      ],
      correct: 1,
    },
    {
      question: "¿Qué sucede si hay un error en el código?",
      answers: [
        "Se ignora",
        "El flujo falla (por defecto)",
        "Se corrige automáticamente",
        "Continúa sin datos",
      ],
      correct: 1,
    },
  ],
  6: [
    {
      question: "¿Qué nodos se combinan típicamente en un workflow completo?",
      answers: [
        "Solo Trigger",
        "Trigger + Edit Fields + IF + HTTP Request + Code",
        "Solo Code",
        "Ninguno",
      ],
      correct: 1,
    },
    {
      question: "¿Puede un workflow tener múltiples ramas?",
      answers: ["No", "Sí, usando IF o Switch", "Solo una", "Nunca"],
      correct: 1,
    },
    {
      question: "¿Qué es un flujo de integración completa?",
      answers: [
        "Solo un nodo",
        "Un workflow que conecta múltiples servicios y procesa datos",
        "Un error",
        "Una variable",
      ],
      correct: 1,
    },
    {
      question: "¿Qué hace especial a un workflow bien diseñado?",
      answers: [
        "Tiene muchos colores",
        "Maneja errores, transforma datos y conecta servicios",
        "Es muy largo",
        "Tiene pocos nodos",
      ],
      correct: 1,
    },
    {
      question: "¿Puede un workflow procesar datos de APIs?",
      answers: [
        "No",
        "Sí, con HTTP Request y Code",
        "Solo texto",
        "Solo números",
      ],
      correct: 1,
    },
    {
      question: "¿Qué nivel de complejidad puede tener un workflow?",
      answers: [
        "Solo básico",
        "Desde simple hasta muy avanzado",
        "Solo avanzado",
        "Ninguno",
      ],
      correct: 1,
    },
    {
      question: "¿Un workflow puede llamar múltiples APIs?",
      answers: ["No", "Sí, con varios nodos HTTP Request", "Solo una", "Nunca"],
      correct: 1,
    },
    {
      question: "¿Un workflow necesita ramificaciones obligatoriamente?",
      answers: [
        "Sí, siempre",
        "No, depende del caso de uso",
        "Nunca",
        "Solo en producción",
      ],
      correct: 1,
    },
    {
      question: '¿Qué hace a un workflow "inteligente"?',
      answers: [
        "Que sea largo",
        "Que tome decisiones basadas en datos",
        "Que tenga muchos nodos",
        "Que use muchos colores",
      ],
      correct: 1,
    },
    {
      question: "¿Puedes crear workflows personalizados?",
      answers: [
        "No",
        "Sí, combinando nodos según tus necesidades",
        "Solo con plantillas",
        "Nunca",
      ],
      correct: 1,
    },
  ],
  7: [
    {
      question: "¿Qué es un Webhook en n8n?",
      answers: [
        "Un tipo de base de datos",
        "Un endpoint HTTP que inicia un workflow cuando recibe datos",
        "Un lenguaje de programación",
        "Un archivo",
      ],
      correct: 1,
    },
    {
      question: "¿Cuántas URLs genera el nodo Webhook?",
      answers: ["Una", "Dos: Test URL y Production URL", "Tres", "Ninguna"],
      correct: 1,
    },
    {
      question: "¿Cuándo usar la Test URL vs Production URL?",
      answers: [
        "Test para desarrollo, Production para workflow publicado",
        "Son iguales",
        "Solo Production",
        "Solo Test",
      ],
      correct: 0,
    },
    {
      question: "¿Qué métodos HTTP soporta el nodo Webhook?",
      answers: [
        "Solo GET",
        "GET, POST, PUT, DELETE y más",
        "Solo POST",
        "Ninguno",
      ],
      correct: 1,
    },
    {
      question: "¿Qué nodo personaliza la respuesta del Webhook?",
      answers: ["Edit Fields", "Respond to Webhook", "Code", "IF"],
      correct: 1,
    },
    {
      question: '¿Qué significa "event-driven"?',
      answers: [
        "Conducido por eventos externos",
        "Conducido por tiempo",
        "Manual",
        "Aleatorio",
      ],
      correct: 0,
    },
    {
      question: "¿Puedes configurar autenticación en un Webhook?",
      answers: [
        "No",
        "Sí, con Basic Auth, Header Auth, etc.",
        "Solo en Cloud",
        "Solo en self-hosted",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué pasa si el workflow no está publicado y usas Production URL?",
      answers: [
        "Funciona igual",
        "No funciona, el webhook no está registrado",
        "Da error 500",
        "Se activa solo",
      ],
      correct: 1,
    },
    {
      question: "¿Los webhooks pueden recibir datos binarios?",
      answers: [
        "No",
        "Sí, configurando Binary Property",
        "Solo texto",
        "Solo JSON",
      ],
      correct: 1,
    },
    {
      question: "¿Para qué sirven los webhooks en automatización?",
      answers: [
        "Decoración",
        "Recibir datos en tiempo real de servicios externos",
        "Nada útil",
        "Solo testing",
      ],
      correct: 1,
    },
  ],
  8: [
    {
      question: "¿Qué hace el nodo Merge?",
      answers: [
        "Divide flujos",
        "Combina datos de múltiples ramas en una salida",
        "Elimina datos",
        "Crea errores",
      ],
      correct: 1,
    },
    {
      question: "¿Cuántas entradas puede tener el nodo Merge?",
      answers: ["Solo 1", "Solo 2", "Dos o más", "Ninguna"],
      correct: 2,
    },
    {
      question: "¿Qué modos tiene el nodo Merge?",
      answers: [
        "Solo Append",
        "Append, Combine, Choose Branch, y más",
        "Solo Combine",
        "Ninguno",
      ],
      correct: 1,
    },
    {
      question: "¿Cuándo usar Merge?",
      answers: [
        "Nunca",
        "Para combinar datos de diferentes ramas del workflow",
        "Solo al inicio",
        "Solo al final",
      ],
      correct: 1,
    },
    {
      question: "¿Qué nodo compara diferencias entre conjuntos de datos?",
      answers: ["Merge", "Compare Datasets", "IF", "Code"],
      correct: 1,
    },
    {
      question: "¿Qué nodo se usa para dividir un array en items individuales?",
      answers: ["Merge", "Split Out", "Edit Fields", "HTTP Request"],
      correct: 1,
    },
    {
      question: "¿Merge espera a que lleguen datos de todas las ramas?",
      answers: ["No", "Sí, por defecto", "Nunca", "Depende del modo"],
      correct: 3,
    },
    {
      question: "¿Puedes tener múltiples nodos Merge?",
      answers: ["No", "Sí", "Solo uno", "Nunca"],
      correct: 1,
    },
    {
      question: "¿Qué pasa si una rama no envía datos a Merge?",
      answers: [
        "Continúa normal",
        "Puede bloquear según configuración",
        "Se ignora siempre",
        "Error fatal siempre",
      ],
      correct: 1,
    },
    {
      question: "¿Para qué sirve combinar datos?",
      answers: [
        "Nada",
        "Crear resultados unificados de múltiples fuentes",
        "Eliminar información",
        "Solo decorar",
      ],
      correct: 1,
    },
  ],
  9: [
    {
      question: "¿Qué hace el nodo Split Out?",
      answers: [
        "Combina datos",
        "Divide un array en múltiples items individuales",
        "Elimina datos",
        "Crea errores",
      ],
      correct: 1,
    },
    {
      question: "¿Qué nodo es el opuesto de Split Out?",
      answers: ["Merge", "Aggregate", "IF", "Code"],
      correct: 1,
    },
    {
      question: "¿Para qué sirve Loop Over Items (Split in Batches)?",
      answers: [
        "Eliminar items",
        "Procesar items en lotes controlados",
        "Crear arrays",
        "Solo ordenar",
      ],
      correct: 1,
    },
    {
      question: "¿Split Out crea múltiples items de salida?",
      answers: [
        "No",
        "Sí, uno por cada elemento del array",
        "Solo dos",
        "Nunca",
      ],
      correct: 1,
    },
    {
      question: "¿Qué ventaja tiene el procesamiento en lote?",
      answers: [
        "Es más lento",
        "Mejor control y rendimiento con muchos datos",
        "Ninguna",
        "Más errores",
      ],
      correct: 1,
    },
    {
      question: "¿Puedes configurar el tamaño del lote en Loop Over Items?",
      answers: ["No", "Sí, con Batch Size", "Solo 10", "Solo 100"],
      correct: 1,
    },
    {
      question: "¿Qué sucede después de Split Out?",
      answers: [
        "El workflow termina",
        "Cada item continúa por el flujo individualmente",
        "Se borran los datos",
        "Error",
      ],
      correct: 1,
    },
    {
      question: "¿Split Out funciona con arrays JSON?",
      answers: [
        "No",
        "Sí, los divide en items separados",
        "Solo strings",
        "Solo números",
      ],
      correct: 1,
    },
    {
      question: "¿Cómo volver a unir items después de Split Out?",
      answers: [
        "No se puede",
        "Con el nodo Aggregate",
        "Automáticamente",
        "Con Edit Fields",
      ],
      correct: 1,
    },
    {
      question: "¿Es útil Split Out para procesar listas de APIs?",
      answers: ["No", "Sí, muy útil", "A veces", "Nunca"],
      correct: 1,
    },
  ],
  10: [
    {
      question:
        "¿Qué nodo reemplazó al nodo Function desde la versión 0.198.0?",
      answers: ["Edit Fields", "El nodo Code", "HTTP Request", "IF"],
      correct: 1,
    },
    {
      question: "¿El nodo Code requiere escribir código?",
      answers: [
        "No, usa funciones predefinidas",
        "Sí, JavaScript o Python",
        "Solo arrastrar",
        "Nunca",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué funciones de transformación están disponibles en expresiones?",
      answers: [
        "Ninguna",
        "isEmail(), extractDomain(), removeDuplicates() y más",
        "Solo suma",
        "Solo texto",
      ],
      correct: 1,
    },
    {
      question: "¿Dónde puedes usar expresiones en n8n?",
      answers: [
        "Solo en Code",
        "En cualquier campo que soporte modo Expression",
        "En ningún lado",
        "Solo en Edit Fields",
      ],
      correct: 1,
    },
    {
      question: "¿Cómo accedes a datos del nodo anterior en una expresión?",
      answers: ["$previous", "{{ $json.campo }}", "$data", "No se puede"],
      correct: 1,
    },
    {
      question: "¿Puedes encadenar funciones de transformación?",
      answers: ["No", "Sí", "Solo dos", "Nunca"],
      correct: 1,
    },
    {
      question: "¿Qué sintaxis usa n8n para expresiones?",
      answers: ["[ ]", "{{ }}", "< >", "( )"],
      correct: 1,
    },
    {
      question: "¿Qué NO puede hacer el nodo Code por sí solo?",
      answers: [
        "Transformar datos",
        "Hacer llamadas HTTP directas (usa HTTP Request)",
        "Calcular",
        "Filtrar",
      ],
      correct: 1,
    },
    {
      question: "¿El nodo Code puede manejar errores?",
      answers: [
        "No",
        "Sí, con try/catch y configuración de error handling",
        "Nunca",
        "Solo con IF",
      ],
      correct: 1,
    },
    {
      question: "¿Cuándo preferir expresiones sobre el nodo Code?",
      answers: [
        "Siempre",
        "Para transformaciones simples en campos individuales",
        "Nunca",
        "Solo debugging",
      ],
      correct: 1,
    },
  ],
  11: [
    {
      question: "¿Qué es integración multi-API?",
      answers: [
        "Una sola API",
        "Conectar múltiples servicios externos en un workflow",
        "Solo HTTP",
        "Nada",
      ],
      correct: 1,
    },
    {
      question: "¿Puedes llamar diferentes APIs en un workflow?",
      answers: [
        "No",
        "Sí, con múltiples nodos HTTP Request",
        "Solo una",
        "Nunca",
      ],
      correct: 1,
    },
    {
      question: "¿Qué es orquestación de servicios?",
      answers: [
        "Música",
        "Coordinar y secuenciar llamadas a múltiples APIs",
        "Una función",
        "Un error",
      ],
      correct: 1,
    },
    {
      question: "¿Cómo pasar datos entre diferentes llamadas API?",
      answers: [
        "No se puede",
        "Los datos fluyen automáticamente entre nodos conectados",
        "Solo manualmente",
        "Con cables físicos",
      ],
      correct: 1,
    },
    {
      question: "¿Qué considerar al integrar múltiples APIs?",
      answers: [
        "Nada",
        "Rate limits, autenticación y manejo de errores",
        "Solo el color",
        "El nombre",
      ],
      correct: 1,
    },
    {
      question: "¿Puedes transformar datos entre llamadas API?",
      answers: ["No", "Sí, con Edit Fields o Code", "Nunca", "Solo texto"],
      correct: 1,
    },
    {
      question: "¿Qué hacer si una API falla?",
      answers: [
        "Ignorar",
        "Configurar error handling en el nodo",
        "Reiniciar todo",
        "Nada",
      ],
      correct: 1,
    },
    {
      question: "¿Es posible hacer llamadas API en paralelo?",
      answers: [
        "No",
        "Sí, con ramas paralelas que luego se unen con Merge",
        "Nunca",
        "Solo 2",
      ],
      correct: 1,
    },
    {
      question: "¿Qué formato es estándar entre APIs modernas?",
      answers: ["PDF", "JSON", "DOC", "XLS"],
      correct: 1,
    },
    {
      question: "¿Por qué integrar múltiples APIs?",
      answers: [
        "Por diversión",
        "Para crear soluciones completas y automatizadas",
        "No es útil",
        "Solo testing",
      ],
      correct: 1,
    },
  ],
  12: [
    {
      question: '¿Qué hace a un workflow "production-ready"?',
      answers: [
        "Ser simple",
        "Ser robusto, con manejo de errores y bien testeado",
        "Tener pocos nodos",
        "No tener lógica",
      ],
      correct: 1,
    },
    {
      question: "¿Qué debes hacer para que un Schedule Trigger funcione?",
      answers: [
        "Nada",
        "Publicar (activar) el workflow",
        "Solo guardarlo",
        "Ejecutarlo manualmente",
      ],
      correct: 1,
    },
    {
      question: "¿Por qué usar ramificaciones en producción?",
      answers: [
        "Por estética",
        "Para manejar diferentes escenarios y casos edge",
        "No es necesario",
        "Solo para errores",
      ],
      correct: 1,
    },
    {
      question: "¿Qué opciones de error handling tiene cada nodo?",
      answers: [
        "Ninguna",
        "Stop Workflow, Continue, Continue (using error output)",
        "Solo Stop",
        "Solo Continue",
      ],
      correct: 1,
    },
    {
      question: "¿Qué trigger ejecuta un workflow cuando otro falla?",
      answers: [
        "Schedule Trigger",
        "Error Trigger",
        "Manual Trigger",
        "Webhook",
      ],
      correct: 1,
    },
    {
      question: '¿Qué significa "production-ready"?',
      answers: [
        "En desarrollo",
        "Listo para uso real con usuarios",
        "Con errores",
        "Incompleto",
      ],
      correct: 1,
    },
    {
      question: "¿Cómo organizar workflows complejos?",
      answers: [
        "Todo en uno",
        "Usando sub-workflows reutilizables",
        "Sin organización",
        "Con muchos colores",
      ],
      correct: 1,
    },
    {
      question: "¿Qué aprendiste en este curso?",
      answers: [
        "Nada",
        "Crear workflows de automatización con n8n",
        "Solo teoría",
        "Solo nombres de nodos",
      ],
      correct: 1,
    },
    {
      question: "¿Dónde puedes ver el historial de ejecuciones?",
      answers: [
        "No existe",
        "En la pestaña Executions",
        "Solo en logs del servidor",
        "En ningún lado",
      ],
      correct: 1,
    },
    {
      question: "¿Cuál es el siguiente paso después de este curso?",
      answers: [
        "Nada",
        "Practicar creando automatizaciones reales",
        "Olvidar todo",
        "Empezar de cero",
      ],
      correct: 1,
    },
  ],
  13: [
    {
      question: "¿Qué permiten las expresiones en n8n?",
      answers: [
        "Referenciar datos dinámicamente",
        "Crear nuevos nodos",
        "Eliminar workflows",
        "Cambiar el color del canvas",
      ],
      correct: 0,
    },
    {
      question:
        "¿Cuál es la sintaxis básica para referenciar un campo JSON en una expresión?",
      answers: ["[campo]", "{{ $json.campo }}", "campo.json", "{{ campo }}"],
      correct: 1,
    },
    {
      question:
        '¿Cómo se referencia un dato de un nodo específico llamado "API_Data"?',
      answers: [
        "{{ $json.API_Data.campo }}",
        '{{ $node["API_Data"].json.campo }}',
        "{{ API_Data.campo }}",
        "{{ $node.API_Data }}",
      ],
      correct: 1,
    },
    {
      question: '¿Qué son las "funciones de transformación" en n8n?',
      answers: [
        "Funciones para modificar strings, arrays o fechas dentro de expresiones",
        "Nodos especiales",
        "Variables globales",
        "Errores de sintaxis",
      ],
      correct: 0,
    },
    {
      question: "¿En qué modo de campo se usan las expresiones?",
      answers: [
        "Solo en texto plano",
        "En modo Expression",
        "Solo en Code",
        "En modo JSON",
      ],
      correct: 1,
    },
    {
      question: "¿Qué pasa si la expresión referencia un campo que no existe?",
      answers: [
        "El workflow se detiene",
        "Devuelve un valor nulo o vacío (depende de la configuración)",
        "Se crea el campo automáticamente",
        "Se ignora la expresión",
      ],
      correct: 1,
    },
    {
      question: "¿Qué tipo de datos se pueden manipular con expresiones?",
      answers: [
        "Strings",
        "Números",
        "Arrays y Objetos JSON",
        "Todos los anteriores",
      ],
      correct: 3,
    },
    {
      question: "¿Qué es el editor de expresiones?",
      answers: [
        "Una herramienta para escribir código JavaScript",
        "Una interfaz gráfica para construir y probar expresiones",
        "Un nodo",
        "Un archivo de configuración",
      ],
      correct: 1,
    },
    {
      question: "¿Las expresiones pueden contener lógica condicional?",
      answers: [
        "No",
        "Sí, usando operadores ternarios (ej: ? :)",
        "Solo con el nodo IF",
        "Solo en Python",
      ],
      correct: 1,
    },
    {
      question: "¿Cuál es el principal beneficio de usar expresiones?",
      answers: [
        "Hacer el workflow más largo",
        "Permitir el flujo de datos dinámico y contextual",
        "Reducir la velocidad",
        "Aumentar la complejidad",
      ],
      correct: 1,
    },
  ],
  14: [
    {
      question: "¿Para qué sirve el nodo Merge?",
      answers: [
        "Combinar datos de múltiples ramas del workflow",
        "Dividir datos",
        "Eliminar datos duplicados",
        "Crear una base de datos",
      ],
      correct: 0,
    },
    {
      question:
        "¿Qué modo de Merge se usa para unir datos de dos ramas que tienen la misma cantidad de items?",
      answers: ["Append", "Combine", "Choose Branch", "Merge By Index"],
      correct: 1,
    },
    {
      question: '¿Qué hace el modo "Append" en Merge?',
      answers: [
        "Combina los items en un solo objeto",
        "Añade los items de la segunda entrada al final de la primera",
        "Elimina duplicados",
        "Compara los items",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué nodo es útil para encontrar diferencias entre dos conjuntos de datos?",
      answers: ["Merge", "Compare Datasets", "IF", "Code"],
      correct: 1,
    },
    {
      question:
        "¿Merge puede combinar datos basados en una clave común (ej. ID)?",
      answers: [
        "No",
        'Sí, usando el modo "Merge By Key"',
        "Solo por índice",
        "Solo por nombre",
      ],
      correct: 1,
    },
    {
      question: "¿Cuántas entradas puede tener el nodo Merge?",
      answers: ["Solo una", "Dos o más", "Máximo tres", "Solo dos"],
      correct: 1,
    },
    {
      question:
        '¿Qué pasa si una rama de entrada no tiene datos en modo "Combine"?',
      answers: [
        "El workflow falla",
        "El item correspondiente se omite o se llena con valores nulos",
        "Se espera indefinidamente",
        "Se duplican los datos de la otra rama",
      ],
      correct: 1,
    },
    {
      question:
        "¿Cuál es el objetivo principal de las operaciones de Join en n8n?",
      answers: [
        "Hacer el workflow más lento",
        "Integrar información relacionada de diferentes fuentes",
        "Separar la información",
        "Solo para debugging",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué nodo se usa para dividir un array en items individuales antes de un Merge?",
      answers: ["Aggregate", "Split Out", "Edit Fields", "Code"],
      correct: 1,
    },
    {
      question: "¿El nodo Merge puede cambiar la estructura de los datos?",
      answers: [
        "No",
        "Sí, al combinar o añadir nuevos campos",
        "Solo si se usa Code",
        "Solo en modo Append",
      ],
      correct: 1,
    },
  ],
  15: [
    {
      question: "¿Qué es un sub-workflow?",
      answers: [
        "Un workflow que se ejecuta dentro de otro workflow",
        "Un error",
        "Un nodo obsoleto",
        "Una variable",
      ],
      correct: 0,
    },
    {
      question: "¿Qué nodo se usa para llamar a un sub-workflow?",
      answers: ["Call Workflow", "Execute Workflow", "Run Sub", "Sub Flow"],
      correct: 1,
    },
    {
      question: "¿Con qué nodo debe iniciar un sub-workflow para ser llamado?",
      answers: [
        "Manual Trigger",
        "Webhook",
        "Execute Workflow Trigger",
        "Schedule Trigger",
      ],
      correct: 2,
    },
    {
      question: "¿Cuál es el principal beneficio de usar sub-workflows?",
      answers: [
        "Hacer el workflow más largo",
        "Modularidad y reutilización de lógica",
        "Consumir más recursos",
        "Aumentar la complejidad",
      ],
      correct: 1,
    },
    {
      question:
        "¿Se pueden pasar datos del workflow principal al sub-workflow?",
      answers: [
        "No",
        "Sí, a través de la configuración del nodo Execute Workflow",
        "Solo variables globales",
        "Solo texto",
      ],
      correct: 1,
    },
    {
      question: "¿El sub-workflow puede devolver datos al workflow principal?",
      answers: [
        "No",
        "Sí, usando el nodo Respond to Workflow",
        "Solo si falla",
        "Solo si es muy corto",
      ],
      correct: 1,
    },
    {
      question: "¿Qué permite el diseño modular de workflows?",
      answers: [
        "Crear flujos más difíciles de entender",
        "Simplificar el mantenimiento y la depuración",
        "Limitar el número de nodos",
        "Evitar el uso de expresiones",
      ],
      correct: 1,
    },
    {
      question:
        "¿Un sub-workflow puede ser llamado por múltiples workflows principales?",
      answers: [
        "No",
        "Sí, es una de sus principales ventajas",
        "Solo si están en el mismo proyecto",
        "Solo si es muy simple",
      ],
      correct: 1,
    },
    {
      question: "¿Qué sucede si el sub-workflow falla?",
      answers: [
        "El workflow principal continúa sin problemas",
        "El nodo Execute Workflow falla, permitiendo manejar el error",
        "Se reinicia automáticamente",
        "Se ignora el error",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué nodo se usa para finalizar un sub-workflow y devolver datos?",
      answers: ["End", "Respond to Workflow", "Stop", "Return"],
      correct: 1,
    },
  ],
  16: [
    {
      question: "¿Qué es la orquestación de APIs?",
      answers: [
        "El diseño de la interfaz de una API",
        "Coordinar y secuenciar múltiples llamadas a APIs en un solo flujo",
        "Un tipo de autenticación",
        "Un error de conexión",
      ],
      correct: 1,
    },
    {
      question: "¿Qué nodo se usa para interactuar con diferentes APIs?",
      answers: [
        "Solo Code",
        "Múltiples nodos HTTP Request",
        "Solo un nodo HTTP Request",
        "El nodo API Orchestrator",
      ],
      correct: 1,
    },
    {
      question: "¿Qué se debe considerar al coordinar múltiples APIs?",
      answers: [
        "Rate limits",
        "Autenticación individual",
        "Manejo de errores para cada servicio",
        "Todas las anteriores",
      ],
      correct: 3,
    },
    {
      question:
        "¿Cómo se manejan los datos que fluyen entre diferentes llamadas API?",
      answers: [
        "Se pierden",
        "Fluyen automáticamente entre nodos conectados",
        "Se deben guardar en una base de datos",
        "Solo con expresiones",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué nodo es esencial para unir los resultados de llamadas API paralelas?",
      answers: ["IF", "Merge", "Code", "Split Out"],
      correct: 1,
    },
    {
      question:
        '¿Qué significa "multi-servicio" en el contexto de automatización?',
      answers: [
        "Usar solo un servicio",
        "Integrar varios servicios o plataformas (ej. Slack, Google Sheets, Stripe)",
        "Usar solo n8n",
        "Solo APIs",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué se puede usar para transformar datos entre una llamada API y la siguiente?",
      answers: ["Edit Fields", "Code", "Ambos", "Ninguno"],
      correct: 2,
    },
    {
      question: '¿Qué es un "rate limit"?',
      answers: [
        "Un límite de velocidad de internet",
        "Un límite en el número de peticiones que puedes hacer a una API en un tiempo dado",
        "Un tipo de error",
        "Una variable",
      ],
      correct: 1,
    },
    {
      question: "¿Cómo se asegura la robustez en la integración multi-API?",
      answers: [
        "Ignorando los errores",
        "Implementando manejo de errores y reintentos",
        "Haciendo solo una llamada",
        "Usando solo GET",
      ],
      correct: 1,
    },
    {
      question: "¿Cuál es el objetivo de la integración multi-API?",
      answers: [
        "Crear soluciones de negocio complejas y automatizadas",
        "Solo probar APIs",
        "Hacer el workflow más lento",
        "Solo para uso personal",
      ],
      correct: 0,
    },
  ],
  17: [
    {
      question: "¿Cómo maneja n8n los archivos?",
      answers: [
        "Como texto plano",
        'Como "binary data"',
        "Como URLs",
        "Como variables",
      ],
      correct: 1,
    },
    {
      question: "¿Qué nodo se usa para leer datos de archivos como CSV o JSON?",
      answers: [
        "Read File",
        "Extract From File",
        "Convert to File",
        "HTTP Request",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué nodo se usa para generar un archivo a partir de datos JSON?",
      answers: ["Read File", "Extract From File", "Convert to File", "Code"],
      correct: 2,
    },
    {
      question:
        "¿Qué nodo se usa para interactuar con el sistema de archivos local?",
      answers: [
        "Read/Write Files from Disk",
        "HTTP Request",
        "Webhooks",
        "Merge",
      ],
      correct: 0,
    },
    {
      question: '¿Qué es un "buffer" en el contexto de archivos?',
      answers: [
        "Un tipo de error",
        "Una región de memoria temporal para datos binarios",
        "Un nodo",
        "Una variable",
      ],
      correct: 1,
    },
    {
      question:
        "¿Puedes subir archivos a servicios como S3 o Google Drive con n8n?",
      answers: [
        "No",
        "Sí, usando los nodos de integración correspondientes",
        "Solo con Code",
        "Solo si son pequeños",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué se puede hacer con un archivo después de leerlo con Extract From File?",
      answers: [
        "Procesar los datos como items de n8n",
        "Solo guardarlo",
        "Eliminarlo",
        "Nada",
      ],
      correct: 0,
    },
    {
      question: "¿Qué tipo de archivos puede manejar n8n?",
      answers: [
        "Solo texto",
        "Imágenes, PDFs, CSV, JSON, y más",
        "Solo JSON",
        "Solo binarios",
      ],
      correct: 1,
    },
    {
      question: "¿Qué se debe configurar al usar Convert to File?",
      answers: [
        "El tipo de archivo de salida (ej. CSV, JSON)",
        "El color",
        "El nombre del autor",
        "La dificultad",
      ],
      correct: 0,
    },
    {
      question: "¿Cuál es el propósito del procesamiento de archivos en n8n?",
      answers: [
        "Automatizar la manipulación y transferencia de documentos y datos",
        "Solo para testing",
        "Hacer el workflow más lento",
        "Solo para imágenes",
      ],
      correct: 0,
    },
  ],
  18: [
    {
      question:
        "¿Qué elementos debe incluir un sistema de automatización complejo?",
      answers: [
        "Triggers, manejo de errores, sub-workflows, integración multi-API",
        "Solo un nodo",
        "Solo triggers",
        "Solo errores",
      ],
      correct: 0,
    },
    {
      question: '¿Qué es un "caso edge" en el diseño de workflows?',
      answers: [
        "Un error fatal",
        "Un escenario poco común o extremo que debe ser manejado",
        "Un nodo especial",
        "Una variable",
      ],
      correct: 1,
    },
    {
      question:
        '¿Por qué es importante el "logging" en la automatización compleja?',
      answers: [
        "Para decorar",
        "Para rastrear la ejecución, depurar y monitorear el sistema",
        "Para hacer el workflow más lento",
        "No es importante",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué se usa para organizar la lógica compleja en un workflow grande?",
      answers: [
        "Un solo nodo Code",
        "Sub-workflows y comentarios/sticky notes",
        "Solo Merge",
        "Solo IF",
      ],
      correct: 1,
    },
    {
      question: '¿Qué significa "production-ready" en este contexto?',
      answers: [
        "Que está listo para ser activado y manejar tráfico real de forma robusta",
        "Que es muy simple",
        "Que tiene pocos nodos",
        "Que es solo un prototipo",
      ],
      correct: 0,
    },
    {
      question: "¿Qué tipo de triggers se usan en automatización compleja?",
      answers: [
        "Solo Manual",
        "Schedule Trigger, Webhook, y otros triggers de apps",
        "Solo Webhook",
        "Ninguno",
      ],
      correct: 1,
    },
    {
      question: '¿Qué es la "integración de sistemas"?',
      answers: [
        "Conectar diferentes aplicaciones y plataformas para que trabajen juntas",
        "Un tipo de error",
        "Un nodo",
        "Una variable",
      ],
      correct: 0,
    },
    {
      question: '¿Qué es la "resiliencia" en un workflow?',
      answers: [
        "La capacidad de fallar rápidamente",
        "La capacidad de recuperarse de errores y continuar la ejecución",
        "La velocidad de ejecución",
        "El número de nodos",
      ],
      correct: 1,
    },
    {
      question: "¿Qué se debe hacer antes de poner un workflow en producción?",
      answers: [
        "Nada",
        "Testear exhaustivamente todos los caminos y casos edge",
        "Solo activarlo",
        "Eliminar el manejo de errores",
      ],
      correct: 1,
    },
    {
      question: "¿Cuál es el objetivo de la Automatización Compleja?",
      answers: [
        "Crear soluciones de negocio robustas y escalables",
        "Solo para aprender",
        "Hacer el workflow más difícil",
        "Solo para uso personal",
      ],
      correct: 0,
    },
  ],
  19: [
    {
      question: '¿Qué es una "best practice" en el diseño de workflows?',
      answers: [
        "Usar nombres descriptivos para nodos",
        "Organizar el canvas con sticky notes",
        "Implementar manejo de errores",
        "Todas las anteriores",
      ],
      correct: 3,
    },
    {
      question: '¿Qué significa "mastery" en n8n?',
      answers: [
        "Haber completado todos los niveles",
        "Dominar la combinación de triggers, lógica, transformación y APIs",
        "Saber solo el nombre de los nodos",
        "Saber solo un tipo de nodo",
      ],
      correct: 1,
    },
    {
      question: '¿Qué es la "optimización" en un workflow?',
      answers: [
        "Hacerlo más lento",
        "Mejorar la eficiencia, reducir el tiempo de ejecución y el consumo de recursos",
        "Añadir nodos innecesarios",
        "Eliminar el manejo de errores",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué tipo de trigger es ideal para un sistema que responde a eventos externos?",
      answers: [
        "Schedule Trigger",
        "Manual Trigger",
        "Webhook",
        "Error Trigger",
      ],
      correct: 2,
    },
    {
      question:
        "¿Qué se debe hacer con un workflow antes de activarlo en producción?",
      answers: [
        "Dejarlo sin guardar",
        "Asegurarse de que todos los credenciales y configuraciones sean correctos",
        "Eliminar los comentarios",
        "Usar solo el modo de prueba",
      ],
      correct: 1,
    },
    {
      question:
        "¿Qué herramienta se usa para documentar partes del workflow en el canvas?",
      answers: [
        "El nodo Documentation",
        "Sticky Notes o comentarios",
        "El nodo Code",
        "El nodo Merge",
      ],
      correct: 1,
    },
    {
      question: '¿Qué es un "flujo completo listo para producción"?',
      answers: [
        "Un prototipo",
        "Un workflow que cumple con todos los requisitos de negocio, es robusto y está optimizado",
        "Un workflow con solo 2 nodos",
        "Un workflow sin manejo de errores",
      ],
      correct: 1,
    },
    {
      question:
        '¿Cuál es el último paso antes de considerar un proyecto como "terminado"?',
      answers: [
        "Dejarlo sin testear",
        "Testear, documentar y activar el workflow",
        "Eliminar el trigger",
        "Solo guardarlo",
      ],
      correct: 1,
    },
    {
      question: "¿Qué es más importante en un workflow de producción?",
      answers: [
        "Que sea muy largo",
        "La robustez y el manejo de errores",
        "Que sea muy simple",
        "Que use solo un nodo",
      ],
      correct: 1,
    },
    {
      question: "¡Felicidades! ¿Qué representa el nivel 19?",
      answers: [
        "El inicio",
        "La culminación y demostración de la maestría en n8n",
        "Un error",
        "Un nivel intermedio",
      ],
      correct: 1,
    },
  ],
};

const LevelQuiz = ({ levelId, onComplete, onReview }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  // Take only first 6 questions
  const allQuestions = quizData[levelId] || [];
  const questions = allQuestions.slice(0, 6);

  const handleAnswer = (answerIndex) => {
    setSelectedAnswer(answerIndex);
  };

  const handleNext = () => {
    if (selectedAnswer === null) {
      toast.error("Por favor selecciona una respuesta");
      return;
    }

    const newAnswers = [...answers, selectedAnswer];
    setAnswers(newAnswers);
    setSelectedAnswer(null);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate score
      const correctCount = newAnswers.reduce((count, answer, index) => {
        return count + (answer === questions[index].correct ? 1 : 0);
      }, 0);
      setScore(correctCount);
      setShowResults(true);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setScore(0);
    setShowResults(false);
    setSelectedAnswer(null);
  };

  const handleReview = () => {
    if (onReview) onReview();
  };

  const handleFinish = () => {
    if (score >= 5) {
      onComplete(true);
    } else {
      toast.error("Necesitas al menos 5/6 para aprobar");
    }
  };

  if (showResults) {
    const passed = score >= 5;
    return (
      <div
        className="glass p-8 rounded-card max-w-2xl mx-auto"
        data-testid="quiz-results"
      >
        <div className="text-center mb-6">
          {passed ? (
            <CheckCircle className="w-20 h-20 text-success mx-auto mb-4" />
          ) : (
            <XCircle className="w-20 h-20 text-error mx-auto mb-4" />
          )}
          <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">
            {passed ? "¡Aprobado!" : "No Aprobado"}
          </h2>
          <p className="text-xl text-text-secondary mb-4">
            Tu puntuación: {score}/6
          </p>
          {passed ? (
            <p className="text-text-primary">
              ¡Excelente trabajo! Has dominado los conceptos de este nivel.
            </p>
          ) : (
            <p className="text-text-primary">
              Necesitas al menos 5 respuestas correctas. Te recomendamos repasar
              el contenido.
            </p>
          )}
        </div>

        <div className="space-y-3">
          {!passed && (
            <button
              onClick={handleReview}
              className="w-full glass py-3 rounded-lg hover:border-primary/50 transition-all flex items-center justify-center gap-2"
              data-testid="review-btn"
            >
              <BookOpen className="w-5 h-5" />
              <span className="font-medium">Repasar Nivel</span>
            </button>
          )}
          <button
            onClick={handleRestart}
            className="w-full glass py-3 rounded-lg hover:border-primary/50 transition-all flex items-center justify-center gap-2"
            data-testid="restart-quiz-btn"
          >
            <RefreshCw className="w-5 h-5" />
            <span className="font-medium">Reintentar Quiz</span>
          </button>
          {passed && (
            <button
              onClick={handleFinish}
              className="w-full btn-neon py-3 rounded-lg font-heading font-bold"
              data-testid="finish-quiz-btn"
            >
              Completar Nivel
            </button>
          )}
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];

  return (
    <div
      className="glass p-8 rounded-card max-w-2xl mx-auto"
      data-testid="level-quiz"
    >
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-heading font-bold text-text-primary">
            Quiz - Nivel {levelId}
          </h2>
          <span className="text-text-secondary">
            Pregunta {currentQuestion + 1}/6
          </span>
        </div>
        <div className="h-2 bg-background-subtle rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{
              width: `${((currentQuestion + 1) / questions.length) * 100}%`,
            }}
            className="h-full bg-gradient-to-r from-primary to-secondary"
          />
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-xl text-text-primary font-medium mb-6">
          {question.question}
        </h3>
        <div className="space-y-3">
          {question.answers.map((answer, index) => (
            <button
              key={index}
              onClick={() => handleAnswer(index)}
              className={`w-full glass p-4 rounded-lg text-left transition-all ${
                selectedAnswer === index
                  ? "border-primary/50 bg-primary/10"
                  : "hover:border-primary/30"
              }`}
              data-testid={`answer-${index}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedAnswer === index
                      ? "border-primary bg-primary"
                      : "border-slate-600"
                  }`}
                >
                  {selectedAnswer === index && (
                    <div className="w-3 h-3 rounded-full bg-white" />
                  )}
                </div>
                <span className="text-text-primary">{answer}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={handleNext}
        disabled={selectedAnswer === null}
        className="w-full btn-neon py-3 rounded-lg font-heading font-bold disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="next-question-btn"
      >
        {currentQuestion < questions.length - 1
          ? "Siguiente Pregunta"
          : "Ver Resultados"}
      </button>
    </div>
  );
};

export default LevelQuiz;
